<script>
  /**
   * Reusable Button Component with Modern Styling
   * @prop {string} variant - 'primary' | 'secondary' | 'success' | 'danger' | 'ghost' | 'outline' | 'glass'
   * @prop {string} size - 'sm' | 'md' | 'lg'
   * @prop {boolean} fullWidth - Makes button full width
   * @prop {boolean} disabled - Disables button
   * @prop {string} type - 'button' | 'submit' | 'reset'
   */
  /**
   * @type {{ variant?: 'primary' | 'secondary' | 'success' | 'danger' | 'ghost' | 'outline' | 'glass', size?: 'sm' | 'md' | 'lg', fullWidth?: boolean, disabled?: boolean, type?: 'button' | 'submit' | 'reset', onclick?: () => void, children?: any }}
   */
  let {
    variant = "primary",
    size = "md",
    fullWidth = false,
    disabled = false,
    type = "button",
    onclick = undefined,
    children,
  } = $props();
</script>

<button
  class="btn btn-{variant} btn-{size}"
  class:btn-full={fullWidth}
  {type}
  {disabled}
  {onclick}
>
  {@render children?.()}
</button>

<style>
  .btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: var(--space-2);
    font-weight: var(--font-medium);
    border: none;
    border-radius: var(--radius-lg);
    cursor: pointer;
    transition:
      background var(--transition-fast),
      transform var(--transition-fast),
      box-shadow var(--transition-fast),
      border-color var(--transition-fast);
    min-height: var(--touch-target-min);
    white-space: nowrap;
    position: relative;
    overflow: hidden;
  }

  .btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none !important;
  }

  .btn:active:not(:disabled) {
    transform: scale(0.98);
  }

  /* Sizes */
  .btn-sm {
    padding: var(--space-2) var(--space-4);
    font-size: var(--text-sm);
    min-height: 36px;
    border-radius: var(--radius-md);
  }

  .btn-md {
    padding: var(--space-3) var(--space-6);
    font-size: var(--text-base);
  }

  .btn-lg {
    padding: var(--space-4) var(--space-8);
    font-size: var(--text-lg);
    border-radius: var(--radius-xl);
  }

  /* Primary - Gradient with glow */
  .btn-primary {
    background: var(--gradient-primary);
    color: white;
    box-shadow: 0 2px 8px rgba(124, 58, 237, 0.25);
  }

  .btn-primary:hover:not(:disabled) {
    box-shadow: 0 4px 16px rgba(124, 58, 237, 0.35);
    transform: translateY(-1px);
  }

  .btn-primary:active:not(:disabled) {
    box-shadow: 0 2px 4px rgba(124, 58, 237, 0.2);
  }

  /* Secondary - Subtle glass effect */
  .btn-secondary {
    background: var(--glass-bg);
    backdrop-filter: blur(var(--blur-sm));
    -webkit-backdrop-filter: blur(var(--blur-sm));
    color: var(--color-gray-700);
    border: 1px solid var(--color-gray-200);
    box-shadow: var(--shadow-sm);
  }

  .btn-secondary:hover:not(:disabled) {
    background: var(--glass-bg-dark);
    border-color: var(--color-gray-300);
    box-shadow: var(--shadow-md);
    transform: translateY(-1px);
  }

  /* Success */
  .btn-success {
    background: linear-gradient(
      135deg,
      var(--color-success-500) 0%,
      var(--color-success-600) 100%
    );
    color: white;
    box-shadow: 0 2px 8px rgba(16, 185, 129, 0.25);
  }

  .btn-success:hover:not(:disabled) {
    box-shadow: 0 4px 16px rgba(16, 185, 129, 0.35);
    transform: translateY(-1px);
  }

  /* Danger */
  .btn-danger {
    background: linear-gradient(
      135deg,
      var(--color-error-500) 0%,
      var(--color-error-600) 100%
    );
    color: white;
    box-shadow: 0 2px 8px rgba(239, 68, 68, 0.25);
  }

  .btn-danger:hover:not(:disabled) {
    box-shadow: 0 4px 16px rgba(239, 68, 68, 0.35);
    transform: translateY(-1px);
  }

  /* Ghost - Minimal */
  .btn-ghost {
    background: transparent;
    color: var(--color-gray-600);
  }

  .btn-ghost:hover:not(:disabled) {
    background: rgba(0, 0, 0, 0.04);
    color: var(--color-gray-800);
  }

  /* Outline - Modern border */
  .btn-outline {
    background: transparent;
    color: var(--color-primary-600);
    border: 1.5px solid var(--color-primary-300);
  }

  .btn-outline:hover:not(:disabled) {
    background: var(--color-primary-50);
    border-color: var(--color-primary-500);
    box-shadow: 0 0 0 3px rgba(139, 92, 246, 0.1);
  }

  /* Glass - Glassmorphism */
  .btn-glass {
    background: var(--glass-bg-light);
    backdrop-filter: blur(var(--blur-md));
    -webkit-backdrop-filter: blur(var(--blur-md));
    color: var(--color-gray-700);
    border: 1px solid var(--glass-border);
    box-shadow: var(--shadow-glass);
  }

  .btn-glass:hover:not(:disabled) {
    background: var(--glass-bg);
    box-shadow: var(--shadow-glass-hover);
    transform: translateY(-1px);
  }

  /* Full Width */
  .btn-full {
    width: 100%;
  }
</style>
